import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * One row of the desk's notice block (GlobalAlerts draws the block around it).
 *
 * Both alerts were the same block of markup twice, down to a gradient and a
 * decorative radial mask painted in amber and rose. Neither survives: a notice
 * is `warn`, and the desk is one flat panel with a hairline like everything
 * else in the console.
 *
 * A single line, about 36px, rather than an icon tile over a heading over a
 * sentence. Each notice used to cost ~70px of the page body for one sentence,
 * and two of them pushed the page itself below the fold on a laptop.
 */

type AlertBannerProps = {
  icon: LucideIcon;
  /** The pill at the start of the line. */
  chip: string;
  heading: string;
  onClose?: () => void;
  children: ReactNode;
};

export const AlertBanner = ({ icon: Icon, chip, heading, onClose, children }: AlertBannerProps) => (
  <div className="flex items-center gap-2.5 px-3 py-1 md:py-1.5">
    <Icon className="text-warn size-4 shrink-0" strokeWidth={1.5} />

    {/* Below md the sentence drops under the chip and heading and wraps there:
        beside a 190px heading on a phone it would get a column a few words wide. */}
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-0.5 md:flex-nowrap">
      <span className="flex shrink-0 items-center gap-2">
        <span className="border-warn-line text-warn rounded-pill border px-2 py-px font-mono text-nano uppercase leading-normal tracking-[0.08em]">
          {chip}
        </span>
        <span className="micro-label">{heading}</span>
      </span>
      <p className="text-foreground min-w-0 basis-full text-body leading-normal md:flex-1 md:basis-auto">{children}</p>
    </div>

    {/* size-9 on a phone, 24px beside a cursor: the console's touch floor. */}
    <button
      type="button"
      onClick={onClose}
      aria-label="Dismiss"
      className="text-dim hover:bg-hover hover:text-foreground focus-visible:ring-ring flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none md:size-6"
    >
      <X className="size-4" strokeWidth={1.5} />
    </button>
  </div>
);
